<template lang="pug">
el-card.section-card
  .sticker-filter-header
    div
      h2.section-title Campaign / sticker insights
      p.section-subtitle All time. The date range above doesn't apply to a single sticker.
    el-select.sticker-select(
      v-model="selectedStickerId"
      filterable
      clearable
      placeholder="Select a campaign or sticker"
      :loading="stickersLoading"
      @change="loadInsights"
    )
      el-option(
        v-for="sticker in stickers"
        :key="sticker.id"
        :label="sticker.title"
        :value="sticker.id"
      )
  .sticker-filter-body(v-if="selectedStickerId")
    .sticker-filter-message(v-if="insightsLoading") Loading sticker insights...
    .sticker-filter-message.text-danger(v-else-if="insightsError") {{ insightsError }}
    sticker-insights(
      v-else-if="insights"
      inline
      :sticker-insights="insights"
    )
  p.sticker-filter-message(v-else-if="!stickersLoading && stickers.length === 0")
    | No published stickers yet.
</template>

<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { ElCard } from 'element-plus';
import globalShared from '@/shared';
import { findOrgStickers, getStickerInsights } from '@/graphs/gql';
import StickerInsights from '@/views/manage-content/media/stickers/StickerInsights.vue';

interface StickerOption {
  id: string;
  title: string;
}

export default defineComponent({
  name: 'StickerInsightsFilter',
  components: { ElCard, StickerInsights },
  props: {
    organizationId: { type: String, default: null },
  },
  setup(props) {
    const { makeGraphRequest, getAuthToken } = globalShared();

    const stickers = ref<StickerOption[]>([]);
    const stickersLoading = ref(false);
    const selectedStickerId = ref<string | null>(null);
    const insights = ref<Record<string, any> | null>(null);
    const insightsLoading = ref(false);
    const insightsError = ref<string | null>(null);

    const loadStickers = async () => {
      stickers.value = [];
      selectedStickerId.value = null;
      insights.value = null;
      if (!props.organizationId) return;

      stickersLoading.value = true;
      try {
        const response = await makeGraphRequest(
          'query',
          findOrgStickers,
          { orgId: props.organizationId },
          getAuthToken(),
        );
        if (response?.errors) {
          console.error('findOrgStickers:', response.errors);
          return;
        }
        stickers.value = (response?.data?.findOrgStickers || [])
          .filter((sticker) => sticker.status !== 'Draft')
          .map((sticker) => ({ id: sticker.id, title: sticker.title || 'Untitled' }))
          .sort((a, b) => a.title.localeCompare(b.title));
      } catch (error) {
        console.error('Error fetching organization stickers:', error);
      } finally {
        stickersLoading.value = false;
      }
    };

    const loadInsights = async (stickerId: string | null) => {
      insights.value = null;
      insightsError.value = null;
      if (!stickerId) return;

      insightsLoading.value = true;
      try {
        const response = await makeGraphRequest(
          'query',
          getStickerInsights,
          { stickerId },
          getAuthToken(),
        );
        if (response?.errors) {
          insightsError.value = response.errors[0]?.message || 'Could not load sticker insights.';
          return;
        }
        insights.value = response?.data?.getStickerInsights || {};
      } catch (error) {
        insightsError.value = `Could not load sticker insights: ${error.message || error}`;
      } finally {
        insightsLoading.value = false;
      }
    };

    watch(() => props.organizationId, loadStickers, { immediate: true });

    return {
      stickers,
      stickersLoading,
      selectedStickerId,
      insights,
      insightsLoading,
      insightsError,
      loadInsights,
    };
  },
});
</script>

<style lang="scss" scoped>
// Same card look as the Dashboard sections (its scoped styles don't reach this component).
.section-card {
  background-color: white;
  border-radius: 16px;
  border: 1px solid #dfdfdf;
  box-shadow: none;

  :deep(.el-card__body) {
    padding: 1.5rem;
  }
}

.sticker-filter-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  flex-wrap: wrap;
  gap: 1rem;
}

.section-title {
  font-size: 1.125rem;
  font-weight: 700;
  color: #111827;
  margin: 0;
}

.section-subtitle {
  font-size: 0.8rem;
  color: #6b7280;
  margin: 0.25rem 0 0;
}

.sticker-select {
  width: 320px;
  max-width: 100%;
}

.sticker-filter-body {
  margin-top: 1rem;
}

.sticker-filter-message {
  margin: 1rem 0 0;
  font-size: 0.875rem;
  color: #6b7280;
}
</style>
